<script setup lang="tsx">
import { computed, ref } from 'vue'
import useSelector from '@/views/chat/components/selector'
import type { SelectorExpose, TreeItem } from '@/types'
//import { ar } from 'vuetify/locale'
import { removeRepeat } from '@/utils/util'

const props = withDefaults(
  defineProps<{
    list?: TreeItem[]
    top?: boolean
  }>(),
  {
    list: () => [],
    default: false,
  }
)
const { filterList, handleFilter: _handleFilter, indexCache } = useSelector(props.list, 3)
const currentIndexes = ref<[number, number]>([0, 0])
const value = ref<string[]>([])

interface FilterItem {
  label: string
  value: string
  realIndex: number
  indexPath?: string
}
// 关键字过滤后的列表
const tagList = computed<FilterItem[]>(() => {
  return Array.from(new Set(filterList.value.map((t) => t.split('-')[0]))).map((index) => {
    return {
      label: props.list[+index].label,
      value: props.list[+index].value,
      realIndex: +index,
    }
  })
})
const categoryList = computed<FilterItem[]>(() => {
  const rule = new RegExp('^' + currentIndexes.value[0] + '-')
  const targetFilterList = filterList.value.filter((t) => rule.test(t)).map((t) => +t.split('-')[1])
  return Array.from(new Set(targetFilterList)).map((index) => {
    const item = props.list[currentIndexes.value[0]].children?.[index]!
    return {
      label: item.label,
      value: item.value,
      realIndex: index,
    }
  })
})
const subList = computed<FilterItem[]>(() => {
  const [p1, p2] = currentIndexes.value
  const rule = new RegExp('^' + currentIndexes.value.join('-') + '-')
  const targetFilterList = filterList.value.filter((t) => rule.test(t)).map((t) => +t.split('-')[2])
  return Array.from(new Set(targetFilterList)).map((index) => {
    const item = props.list[p1].children?.[p2].children?.[index]!
    return {
      label: item.label,
      value: item.value,
      realIndex: index,
      indexPath: `${p1}-${p2}-${index}`,
    }
  })
})

// 点击选中/取消逻辑
function handleSelect(item: FilterItem) {
  const indexes = indexCache.value[item.value!]
  if (value.value.includes(indexes)) {
    value.value.splice(value.value.indexOf(indexes), 1)
  } else {
    value.value.push(indexes)
  }
}

// 为modal暴露的取值方法
function getResult() {
  return value.value.map((item) => {
    const [i1, i2, i3] = item.split('-').map((t) => +t)
    return props.list[i1].children?.[i2].children?.[i3].value
  })
}

// 是否选择
function isSubItemActive(index: number) {
  const indexes = [...currentIndexes.value, index].join('-')
  return value.value.includes(indexes)
}

// 组件： 数标
function CategoryBadge(props: { indexes: string }) {
  const prefix = props.indexes + '-'
  let count = 0
  value.value.forEach((t) => new RegExp('^' + prefix).test(t) && count++)
  return count ? <div class="category-badge">{count}</div> : null
}

// 关键字过滤
function handleFilter(keywords: string) {
  _handleFilter(keywords)
  const arr = filterList.value[0].split('-').map((t) => +t)
  if (!arr.length) {
    arr.push(0, 0)
  }
  currentIndexes.value[0] = +arr[0]
  currentIndexes.value[1] = +arr[1]
}

function handleSelectAll() {
  const flag = isSelectAll.value
  const subIndexes = subList.value.map((item) => item.indexPath!)
  const others = value.value.filter((item) => !subIndexes.includes(item))
  if (flag) {
    // 取消全选
    value.value = [...others]
  } else {
    // 取消
    value.value = [...subIndexes, ...others]
  }
}

const isSelectAll = computed(() =>
  subList.value.reduce((c1, c2) => {
    return c1 && value.value.includes(indexCache.value[c2.value!])
  }, true)
)

const tagCache = computed(() => {
  const result: { [p: string]: string[] } = {}
  props.list.forEach((t, i1) => {
    result[t.value] = []
    t.children?.forEach((t2, i2) => {
      t2.children?.forEach((t3, i3) => {
        result[t.value].push([i1, i2, i3].join('-'))
      })
    })
  })
  return result
})

const tagCheckedStatus = computed(() => {
  const result: { [p: string]: boolean } = {}
  const tags = tagCache.value
  props.list.map((t) => {
    for (const item of tags[t.value]) {
      if (!value.value.includes(item)) {
        result[t.value] = false
        return
      }
    }
    result[t.value] = true
  })
  return result
})

function tagToggle(tag: string) {
  const cache = tagCache.value[tag]
  const status = tagCheckedStatus.value[tag]
  if (status) {
    value.value = value.value.filter((t) => !cache.includes(t))
  } else {
    value.value = removeRepeat([...value.value, ...cache])
  }
}

defineExpose<SelectorExpose<string[]>>({
  getResult,
  handleFilter,
  setValue(v) {
    v.forEach((item) => {
      value.value.push(indexCache.value[item])
    })
  },
})
</script>

<template>
  <div class="selector-component product-selector" :class="{ 'not-top': !top }">
    <div class="tag-list" v-if="top">
      <template v-for="item in tagList">
        <div
          class="tag-item"
          :class="{ active: currentIndexes[0] === item.realIndex }"
          @click="currentIndexes[0] = item.realIndex"
        >
          <v-icon @click.stop="tagToggle(item.value)" icon="mdi-check" v-if="tagCheckedStatus[item.value]" />
          <span @click.stop="tagToggle(item.value)" class="v-icon" v-else />
          <span>{{ item.label }}</span>
        </div>
      </template>
    </div>
    <div class="category-selector clearfix">
      <div class="category-list pull-left">
        <template v-for="item in categoryList">
          <div
            class="list-item category-item ellipsis"
            :class="{ active: currentIndexes[1] === item.realIndex }"
            @click="currentIndexes[1] = item.realIndex"
          >
            <span>{{ item.label }}</span>
            <category-badge :indexes="`${currentIndexes[0]}-${item.realIndex}`" />
          </div>
        </template>
      </div>
      <div class="sublist">
        <div class="list-item" :class="{ active: isSelectAll }" @click="handleSelectAll">
          <div>全选</div>
          <v-icon icon="mdi-check" v-if="isSelectAll" />
          <span class="v-icon" v-else />
        </div>
        <template v-for="item in subList" :key="`${currentIndexes[0]}-${currentIndexes[1]}-${item.realIndex}`">
          <div class="list-item" :class="{ active: isSubItemActive(item.realIndex) }" @click="handleSelect(item)">
            <div class="ellipsis">{{ item.label }}</div>
            <v-icon icon="mdi-check" v-if="isSubItemActive(item.realIndex)" />
            <span class="v-icon" v-else />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use 'selector';

.product-selector {
  height: 100%;

  &.not-top {
    .category-selector {
      height: 100%;
    }
  }

  .tag-list {
    border-bottom: 1px solid #e9e9e9;
    overflow-x: auto;
    white-space: nowrap;

    &::-webkit-scrollbar {
      display: none;
    }

    .tag-item {
      position: relative;
      display: inline-block;
      line-height: 2rem;
      border-bottom: 2px solid transparent;
      box-sizing: border-box;
      padding: 0 1rem 0 2.2rem;

      .v-icon {
        left: 1rem;
      }

      &.active {
        border-color: var(--primary-color);
      }
    }
  }

  .category-badge {
    display: inline-block;
    position: absolute;
    top: 0.7rem;
    right: 0.3rem;
    padding: 0 0.4rem;
    line-height: 0.85rem;
    background: #ee0a24;
    border-radius: 1.07rem 1.07rem 1.07rem 1.07rem;
    font-size: 0.59rem;
    font-weight: 500;
    color: #ffffff;
  }

  .category-selector {
    height: calc(100% - 2.5rem);
    position: relative;
  }

  .category-list {
    position: absolute;
    left: 0;
    top: 0;
    width: 7.47rem;
    height: 100%;
    background-color: #f3f3f3;
    overflow-y: auto;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .category-item {
    font-size: 0.64rem;
    padding: 0 1.6rem 0 0.8rem;
    line-height: 2.35rem;
    position: relative;

    &.active {
      background-color: #f9f9f9;
    }
  }

  .sublist {
    width: 100%;
    padding-left: 7.47rem;
    height: 100%;
    overflow-y: auto;
    .list-item {
      font-size: 0.64rem;
      padding: 0 1.7rem 0 0.5rem;
    }
  }
}
</style>
